import { type FC, type ReactNode, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import type { EnhancedManCodeId } from '#api/enhancements/enhancedTmsApiTypes'
import { appRoutes } from '#app/navigation/routes/routes'
import { useAppNavigation } from '#app/navigation/useAppNavigation'
import { useAppSelector } from '#app/store'
import type { LocalConfigurationId } from '#app/store/slices/configurationSlice'
import { InternalConfigContext } from './InernalConfigContext'
import { isLocalConfigurationId, isTmsConfigurationId } from './useActiveConfiguration'

export const codeMatcher = /^(?:(?<manCode>TMS-\w+):(?<sortOrder>\d+))|(?<localId>MAIC-\w+)$/

export type GroupResult =
  | {
      manCode: EnhancedManCodeId
      sortOrder: string
      localId: undefined
    }
  | { localId: LocalConfigurationId; manCode: undefined; sortOrder: undefined }
  | undefined

const useNavigateFromMissingConfiguration = (id: string | undefined) => {
  const configurations = useAppSelector((state) => state.truckConfigurations.configurations)
  const recoverableConfigurations = useAppSelector((state) => state.truckConfigurations.recoverableConfigurations)
  const { navigate } = useAppNavigation()

  useEffect(() => {
    if (id && !isLocalConfigurationId(id) && !isTmsConfigurationId(id)) {
      // toastError(new Error(`Invalid configuration Id ${id}, redirecting to setup`))
      navigate(appRoutes.setup.make())
      return
    }

    // redirect to the setup page if no configuration is found
    if (isLocalConfigurationId(id) && !configurations[id] && !recoverableConfigurations.find((x) => x.id === id)) {
      // toastError(new Error(`Configuration id ${id} not found, redirecting to setup`))
      navigate(appRoutes.setup.make())
      return
    }
  }, [configurations, recoverableConfigurations, navigate, id])
}

export const ConfigurationContext: FC<{ children?: ReactNode }> = ({ children }) => {
  const { '*': route } = useParams()
  const params = route?.split('/')
  const id = (params?.length ?? 0) > 0 ? params?.[0] : undefined
  const configurationId = isLocalConfigurationId(id) ? (id as LocalConfigurationId) : isTmsConfigurationId(id) ? (id as EnhancedManCodeId) : undefined

  useNavigateFromMissingConfiguration(id)

  return <InternalConfigContext.Provider value={{ configurationId }}>{children}</InternalConfigContext.Provider>
}
